<script lang="ts">
    
    import { getContext, onDestroy, onMount } from "svelte";
    import { navigate } from "svelte-navigator";
    import { Alert, FormGroup, Icon, Label, Modal, ModalBody, ModalHeader } from "sveltestrap";
    import Achievements from "../../Components/Game/Achievements.svelte";
    import GameHistoric from "../../Components/Game/GameHistoric.svelte";
    import GameMetaInfo from "../../Components/Game/GameMetaInfo.svelte";
    import GameMexHistoric from "../../Components/Game/GameMexHistoric.svelte";
    import Win from "../../Components/Game/Win.svelte";
    import Button from "../../Components/Manager/Button.svelte";
    import Menu from "../../Components/Manager/Menu.svelte";
    import PlayersRanking from "../../Components/Manager/PlayersRanking.svelte";
    import RoomBar from "../../Components/Rooms/RoomBar.svelte";
    import PlayIcon from "../../Components/Shared/Icons/PlayIcon.svelte";
    import TopBar from "../../Components/Shared/Mobile/TopBar.svelte";
    import Watch from "../../Components/Watch.svelte";
    import type { Fn, GameType, RealTimeEvent } from "../../global";
    import type { Game } from "../../Model/Game";
    import type { Player } from "../../Model/Player";
    import { authorizationService, gamesService, meService, monitorScreenService, playersFactory, realTimeService, roomsService } from "../../Services";
    import { i18n } from "../../Services/i18n";
    import RightMenuBtn from "./RightMenuBtn.svelte";
    import { reload } from "../../Services/ReloadService";
    import type { Room } from "../../Model/Room";
    import type { EventsChannel } from "../../Services/EventsChannel";

    const gameHistoricComponentsMap = {
        'BingoMex'              : GameMexHistoric,
        'BingoCartoon'          : GameMexHistoric,
        'BingoAnimals'          : GameMexHistoric,
        'Movies'                : GameMexHistoric,
        'MoviesKids'            : GameMexHistoric,
        'XMas'                  : GameMexHistoric,
        'Football'              : GameMexHistoric,
        'Halloween'             : GameMexHistoric,
        'Superheroes'           : GameMexHistoric,
        'Cartoon'               : GameMexHistoric,
        'default'               : GameHistoric,
    };
    const gameNumbersLimit: Record<GameType, number> = {
      'Bingo90'             : 90,
      'Bingo75'             : 75,
      'BingoMex'            : 54,
      'BingoAnimals'        : 54,
      'BingoCartoon'        : 54,
      'Movies'              : 54,
      'MoviesKids'          : 54,
      'XMas'                : 54,
      'Football'            : 54,
      'Halloween'           : 54,
      'Superheroes'         : 54,
      'Cartoon'             : 54
    };
    const getGame: () => Game = getContext('getGame');
    const clearGame: () => void = getContext('clearGame');
    const toggleRoomSettingsModal: Fn = getContext('toggleRoomSettingsModal');
    const game = getGame();
    const candidateNumbers = createCandidateNumbers();
    const cooldownTime = 3000;
    const gameEventsChannel = game.getEventsChannel();
    const numbersLimit = gameNumbersLimit[game.type];
    const me = meService.me;

    let numbersOut = [];

    let openRightMenu = false;
    let nextBtnDisabled = game.numbers.value.length === numbersLimit;
    let isCooldown = false;
    let winner: Player;
    let finalize = false;
    let reloading = false;
    let openEndGameModal: boolean = false;
    let cancelMotive = null;
    let room: Room;
    let roomEventsChannel: EventsChannel;
    let showCancelOptions: boolean = false;

    $: reload.subscribe(value => reloading = value);
    $: nextBtnDisabled = reloading;

    gameEventsChannel.on('bingo', onBingo);

    onMount(async () => {
        try {
            room = await roomsService.getRoom(game.roomId);
            roomEventsChannel = room.getEventsChannel();

            roomEventsChannel.on('updated', onUpdated);
            gameEventsChannel.on('add_number', onAddNumber);
        } catch (error) {
            console.error(error);
        }
    })

    function onUpdated() {
        location.reload();
    }

    onDestroy(() => {
        gameEventsChannel.destroy();
        realTimeService.end();
    });

    function nextNumber(): void {
        if (nextBtnDisabled) {
            return;
        }
        disableNext();
        
        const index = Math.floor(candidateNumbers.length * Math.random());
        const [number] = candidateNumbers.splice(index, 1);
        game.addNumber(number);
        
        realTimeService.send('game_add_number', {
            gameId: game.id,
            number,
            numbers: game.numbers.value
        });
    }

    function sendHalfNumbers() {
        let limit = 0;
        if(candidateNumbers.length == gameNumbersLimit[game.type]) {
            limit = gameNumbersLimit[game.type] / 2;
        } else {
            limit = candidateNumbers.length + 1;
        }
        
        for(let n = 1; n <= limit; n++) {
            const index = Math.floor(candidateNumbers.length * Math.random());
            const [number] = candidateNumbers.splice(index, 1);
            game.addNumber(number);
            realTimeService.send('game_add_number', {
                gameId: game.id,
                number,
                numbers: game.numbers.value
            });
        }
    }

    function disableNext(): void {
        nextBtnDisabled = game.numbers.value.length === numbersLimit;

        if (nextBtnDisabled) {
            //hemos alcanzado el límite de números para este juego
            return;
        }

        nextBtnDisabled = true;
        isCooldown = true;
        setTimeout(() => {
            nextBtnDisabled = false;
            isCooldown = false;
        }, cooldownTime);
    }

    function createCandidateNumbers(): number[] {
        const limit = gameNumbersLimit[game.type];
        const numbers: number[] = [];
        for (let n = 1; n <= limit; n++) {
            if (!game.hasNumber(n)) {
                numbers.push(n);
            }
        }
        localStorage.setItem('candidateNumbers', JSON.stringify(numbers))
        return numbers;
    }

    function toggleEndGameOptions(): void {
        showCancelOptions = !showCancelOptions;
        cancelMotive = null;
    }

    function toggleEndGameModal(): void {
        openEndGameModal = !openEndGameModal;
        cancelMotive = null;
    }

    async function endGame(motive: string): Promise<boolean> {
        if (!confirm($i18n("¿Seguro que desea cancelar el juego actual?"))) {
            return false;
        }
        try {
            openRightMenu = false
            cancelMotive = motive
            await gamesService.cancelGame(game.roomId, game.id, cancelMotive);
            realTimeService.send('set_idle', { roomId: game.roomId });
            clearGame();
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    }

    function onBingo({ data }: RealTimeEvent): void {
        winner = playersFactory.createPlayerFromDto(data.player);
    }

    function toggleRightMenu(): void {
        openRightMenu = !openRightMenu;
    }

    async function onLogout(): Promise<void> {
        if (await endGame('logout')) {
            authorizationService.clearToken();
            navigate('/');
        }
    }

    function onContinue(): void {
        realTimeService.send('finalize_game', { gameId: game.id });
        finalize = true;
    }

    function openMonitor(): void {
        monitorScreenService.openMonitor(game.roomId);
        openRightMenu = false;
    }

    function finalizeGame(): void {
        realTimeService.send('set_idle', { roomId: game.roomId });
        clearGame();
    }

    async function toggleQr() {
        room.showRoomQrInMonitor.set(!room.showRoomQrInMonitor.value);
        await roomsService.saveRoom(room);
    }

    function onAddNumber({ data }: RealTimeEvent): void {
        /* game.addNumber(data.number); */
        data.game_numbers.map(number => {
            if(!game.hasNumber(number)) {
                game.addNumber(number);
            }
        })
    }

</script>

<TopBar>
    <RightMenuBtn on:click={toggleRightMenu} />
</TopBar>

<div class="content">
    
    <div class="room-bar">
        <RoomBar roomId={game.roomId}>
            <GameMetaInfo {game} />
        </RoomBar>
    </div>

    <div class="game-state">
        {#if winner}
                    
            <div class="win-container">
                <Win player={winner} {game} />
            </div>

            {#if finalize}
                <Button on:click={finalizeGame}>
                    {$i18n("Salir")}
                </Button>
            {:else}
                <Button on:click={onContinue}>
                    {$i18n("Continuar")}
                </Button>
            {/if}
        {:else}

            <div class="game-historic-container notranslate">
                <svelte:component this={gameHistoricComponentsMap[game.type] || gameHistoricComponentsMap.default} {game} tail={3} />
            </div>

            <div class="game-control">
                <Watch state={game.numbers} let:value={gameNumbers}>
                    {#if gameNumbers.length < numbersLimit}
                        <div style="display: flex; gap: 10px">
                            <Button
                                on:click={nextNumber}
                                disabled={nextBtnDisabled}
                                style="padding: 6px; min-width: 150px">
                                <PlayIcon />
                                {#if isCooldown}
                                    <div class="cooldown" style="--cooldownTime: {cooldownTime}ms"></div>
                                {/if}
                            </Button>
                            {#if $me && $me.email.value == 'animador@binfun.com'}
                                <Button
                                    on:click={sendHalfNumbers}
                                    style="padding: 6px; min-width: 150px"
                                >
                                    Half
                                </Button>
                            {/if}
                        </div>
                    {:else}
                        <Alert color="light" class="text-center">
                            THE END
                        </Alert>
                    {/if}
                </Watch>
            </div>
        {/if}
    </div>
    
    <Achievements gameId={game.id} />

</div>

{#if reloading}
    <div class="position-container">
        <div class="centered-div">
        <div style='display:flex; gap: 10px;align-items: start;margin-bottom: 10px;'>
            <span style='font-size: 20px'><Icon name="exclamation-triangle-fill" /></span>
            <span>{$i18n("Parece que has perdido la conexión. Por favor, recarga la página")}</span>
        </div>
        <button class='btn btn-warning' on:click={() => location.reload()}>Reload</button>
        </div>
    </div>
{/if}

<Menu isOpen={openRightMenu} toggle={toggleRightMenu} on:logout={onLogout}>
    
    <div class="d-flex gap-2 mb-3 notranslate">
        {#if winner}
            <Button on:click={clearGame}>
                {$i18n("Terminar")}
            </Button>
        {:else}
             <!-- toggleEndGameModal -->
             <div class='position-relative'>
                <Button on:click={toggleEndGameOptions}>
                    {$i18n("Terminar juego")}
                </Button>
                {#if showCancelOptions}
                    <div class='bg-white position-absolute d-flex flex-column rounded-3' style='z-index: 20;'>
                        <button on:click={() => endGame('training')} class='btn btn-light'>{$i18n("Formación")}</button>
                        <button on:click={() => endGame('game_change')} class='btn btn-light'>{$i18n("Cambiar juego")}</button>
                        <button on:click={() => endGame('network')} class='btn btn-light'>{$i18n("Internet")}</button>
                        <button on:click={() => endGame('game_test')} class='btn btn-light'>{$i18n("Pruebas")}</button>
                        <button on:click={toggleEndGameModal} class='btn btn-light'>{$i18n("Otros")}</button>
                    </div>
                {/if}
             </div>
            
        {/if}

        <Button on:click={openMonitor} class="flex-shrink-0notranslate">
            Monitor
        </Button>
    </div>

    <div class="mb-3 notranslate">
        <Button on:click={toggleRoomSettingsModal}>
            {$i18n("Ajustes de la sala")}
        </Button>
    </div>

    {#if room}
        <div class="mb-3 notranslate">
            <Button on:click={toggleQr}>
                {room.showRoomQrInMonitor.value ? $i18n("Ocultar QR") : $i18n("Mostrar QR")}
            </Button>
        </div>
    {/if}

    <div class="players-list notranslate"> <!-- hidden={showQr} -->
        <h6>{$i18n("Jugadores")}</h6>
        <!-- <RoomPlayersList roomId={game.roomId} let:player>
            <PlayerDetail {player} />
        </RoomPlayersList>-->
        <PlayersRanking roomId={game.roomId} />
    </div>

   <!--  <div class='mt-3 container notranslate' hidden={!showQr}>
        <canvas width="300" height="300" bind:this={canvas}></canvas>
        <div class="game-code">
            <div class="code">{game?.code || '----'}</div>
        </div>
    </div> -->
    
</Menu>

<Modal isOpen={openEndGameModal}>
    <ModalHeader toggle={toggleEndGameModal}>
        {$i18n("Cancelación de juego")}
    </ModalHeader>
    <ModalBody> 
        <form>
            <FormGroup>
                <Label>{$i18n("¿Por qué quieres cancelar este juego?")}</Label>
                <input type="text" class="form-control" bind:value={cancelMotive}>
            </FormGroup>
            <!-- <Button type="submit" disabled={!cancelMotive}>{$i18n("Cancelar juego")}</Button> -->
            <button class='btn btn-warning' type="submit" disabled={!cancelMotive} on:click={() => endGame(cancelMotive)}>{$i18n("Cancelar juego")}</button>
        </form>
    </ModalBody>
</Modal>

<style lang="scss">
    .position-container::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0,0,0,0.5); /* Semi-transparent backdrop */
        z-index: 10;
    }

    /* Centered div */
    .centered-div {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 10;
        display: flex;
        flex-direction: column;
        background-color: #dc3545;
        padding: 20px;
        border-radius: 15px;
        width: 65%;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px
    }

    .content {
        flex: 1 1 0;
        display: flex;
        flex-direction: column;
        padding: 1rem;
        gap: 1rem;
    }

    .room-bar {
        margin: -1rem -1rem 0;
        font-family: ManagerTitle;
        font-size: 1.6rem;
    }

    .game-state {
        flex: 1 1 0;
        display: flex;
        flex-direction: column;
        position: relative;
        gap: 1rem;
    }

    .game-historic-container {
        flex: 1 1 0;
    }

    .win-container {
        flex: 1 1 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    .game-control {
        display: flex;
        justify-content: center;
    }

    .players-list {

        :global(.player-detail) {
            margin-bottom: 0.5rem;
        } 

    }

    .cooldown {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.5);
        animation: cooldown var(--cooldownTime) linear;
    }

    @keyframes cooldown {
        from {
            width: 100%;
        }
        to {
            width: 0%;
        }
    }

    .container {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 90%;
        background-color: #fff;

        canvas {
            max-width: 100%;
        }
    }

    .game-code {
        margin: 2vw 0;

        .code {
            border: 0.4vw solid #00152E;
            color: #00152E;
            font-size: 5vw;
            padding: 0 2vw;
            border-radius: 1vw;
        }
    }

    .button {
        position: relative;
        font-family: ManagerButton;
        line-height: 1.2em;
        padding: 0.75rem 1rem 0.25rem;
        font-size: min(6.5vw, 1.7rem);
        border: none;
        color: var(--color-primary);
        border-radius: 0.25rem;
        background: var(--color-secondary);
        background: linear-gradient(180deg, rgba(255,214,51,1) 0%, rgba(255,214,51,1) 49%, rgba(239,150,22,1) 100%);
        overflow: hidden;

        &:disabled {
            filter: grayscale(1);
        }
    }

</style>
